/**
 * The rules a new character starts by, shared so the server's builder, the
 * store that reloads a character, and the power baseline agree: the starter
 * skills, the body model a character without one shows, and how many item
 * slots a character has.
 */
import { BASE_CARRIED_SLOTS, BASE_STORAGE_SLOTS, MAX_CARRIED_SLOTS } from './constants';
import { createCatalogSkill, findSkillCatalogEntry } from './skill-catalog';
import { resolveCharacterModelSelection } from './character-system';
import type { BodyPart, CharacterModelSelection } from './types/character-system';
import type { CharacterEquipmentLoadout, Equipment, Skill } from './types/game';

/** The skills every new character starts with, untrained. */
export const STARTER_SKILL_IDS = ['blade-mastery', 'battle-instincts'] as const;

/** The body model a character without a chosen one shows (game-content/data/characters.json). */
export const DEFAULT_CHARACTER_MODEL_ID = 'quaternius_male';

/** The bodies a new character may be made with (game-content/data/characters.json). */
export const STARTER_CHARACTER_MODEL_IDS: readonly string[] = ['quaternius_male', 'quaternius_female'];

/** The cosmetic slots a new character picks a body part for; clothing comes from the starting armor. */
export const CHARACTER_APPEARANCE_SLOTS: readonly string[] = ['hair', 'eyebrows', 'facial-hair'];

/**
 * Whether a body part (or any visual attachment, such as an item model)
 * goes in `slot` on the body `characterModelId`; one that names no bodies
 * fits every body.
 */
export function isBodyPartCompatible(
    part: { readonly slot?: string | readonly string[]; readonly compatibleCharacterModelIds?: readonly string[] },
    slot: string,
    characterModelId: string,
): boolean {
    const slots = typeof part.slot === 'string' ? [part.slot] : part.slot ?? [];
    return slots.includes(slot)
        && (!part.compatibleCharacterModelIds || part.compatibleCharacterModelIds.includes(characterModelId));
}

/**
 * Why a new character's look is refused, or undefined when it may be made:
 * a starter body, and in each appearance slot a body part (from
 * `bodyParts`, game-content/data/body-parts.json) made for that slot on
 * that body. Character creation's draft and the server both ask it.
 */
export function findCharacterAppearanceError(selection: CharacterModelSelection, bodyParts: readonly BodyPart[]): string | undefined {
    const { characterModelId, outfit } = resolveCharacterModelSelection(selection);
    if (!STARTER_CHARACTER_MODEL_IDS.includes(characterModelId)) {
        return `A new character can't be made with the body ${characterModelId}.`;
    }
    for (const [slot, partId] of Object.entries(outfit)) {
        if (partId === undefined || partId === '') {
            continue;
        }
        if (!CHARACTER_APPEARANCE_SLOTS.includes(slot)) {
            return `A new character picks nothing for ${slot}.`;
        }
        const part = bodyParts.find((candidate) => candidate.id === partId);
        if (!part || !isBodyPartCompatible(part, slot, characterModelId)) {
            return `${partId} doesn't go in ${slot} on ${characterModelId}.`;
        }
    }
    return undefined;
}

/** The starter skills at level 1, each a fresh copy built from the skill catalog. */
export function createStarterSkills(): Skill[] {
    return STARTER_SKILL_IDS.map((id) => {
        const entry = findSkillCatalogEntry(id);
        if (!entry) {
            throw new Error(`Starter skill ${id} is missing from the skill catalog.`);
        }
        return createCatalogSkill(entry);
    });
}

/** What adds carried slots beyond the base: Strength, the account's tier, and gear that grants loadout slots. */
export interface CarriedSlotSources {
    readonly strength: number;
    readonly accountSlotBonus?: number;
    readonly equippedSlotBonus?: number;
}

/** How many carried slots a character has: the base plus Strength and the bonuses, at most `MAX_CARRIED_SLOTS`. */
export function resolveMaxCarriedSlots({ strength, accountSlotBonus = 0, equippedSlotBonus = 0 }: CarriedSlotSources): number {
    const slots = BASE_CARRIED_SLOTS + Math.max(accountSlotBonus, 0) + strength + Math.max(equippedSlotBonus, 0);
    return Math.min(Math.max(slots, 0), MAX_CARRIED_SLOTS);
}

/** The carried slots a loadout's gear grants, counted at full strength (a weakened bonus would cut off slots and the items in them). */
export function resolveEquippedSlotBonus(equipped: CharacterEquipmentLoadout): number {
    const items: Array<Equipment | null> = [...equipped.weapons, equipped.armor, ...equipped.miscellaneous];
    return items.reduce((sum, item) => sum + Math.max(0, item?.bonuses.loadoutSlots ?? 0), 0);
}

/** How many storage slots a character has: the base plus the account's tier bonus. */
export function resolveMaxStoredSlots(accountSlotBonus = 0): number {
    return BASE_STORAGE_SLOTS + Math.max(accountSlotBonus, 0);
}

/** Whether an account holding `characterCount` characters may create another; a `maxCharacters` of -1 is no limit. */
export function canCreateCharacter(maxCharacters: number, characterCount: number): boolean {
    return maxCharacters < 0 || characterCount < maxCharacters;
}

/** Why a new character's name is refused, or undefined when it may be used: it needs more than spaces. */
export function findCharacterNameError(name: string): string | undefined {
    return name.trim().length > 0 ? undefined : 'A character needs a name.';
}
